/**
 * -------------------------------------------------------------
 * File: featureUtils.ts
 * Description:
 *  - Combines features from multiple package products in API response
 *  - Removes duplicate features based on `feature_key`
 *  - Filters out disabled / invalid features
 *
 * Rules:
 *  - Ignore boolean features when value is "false" or "-1"
 *  - Ignore limit features when value is "0"
 *
 * Author: Harvinder Singh
 * Created On: 09-Feb-2026
 * -------------------------------------------------------------
 */

import { useGlobalTrialState } from "../composable/freeTrail/useGlobalTrialState"

export interface FeatureConfig {
  feature_key: string
  display_name: string
  value_type: "boolean" | "limit" | string
  value: string | number
}

/**
 * Package returned for subscribed products
 * (core, mmc, msc, mcc, etc.)
 */
export interface PackageFeature {
  product_name: string
  renewal_date: string | null
  start_date: string | null
  subscription_type: string
  features: FeatureConfig[] | null
}

/**
 * API response shape.
 *
 * Most keys (core, mmc, msc, mcc, etc.) are PackageFeature objects.
 * add_ons is an array of FeatureConfig objects.
 */
export interface ApiResponse {
  data: Record<string, PackageFeature> & {
    add_ons?: FeatureConfig[]
  }
}

/**
 * Combines and normalizes features from all subscribed packages.
 *
 * Features are collected from:
 * - core
 * - mmc
 * - msc
 * - mcc
 * - add_ons
 *
 * Rules:
 * - Skip disabled boolean features
 * - Skip limit features with value 0
 * - Skip lead_ads for customer preferred UID accounts
 * - Remove duplicate features (first occurrence wins)
 */
export function getCombinedFeatures(
  response: ApiResponse,
  isCustomerPreferredUid: boolean
): FeatureConfig[] {
  const featureMap = new Map<string, FeatureConfig>()

  /**
   * Normalize every source into a common shape:
   * {
   *   features: FeatureConfig[]
   * }
   *
   * This allows the feature-processing logic to remain the same
   * regardless of whether the source is a product package or add-ons.
   */
  const packages = [
    ...Object.values(response.data)
      .filter((item): item is PackageFeature => !Array.isArray(item))
      .map(pkg => ({
        features: pkg.features ?? [],
      })),
    {
      features: response.data.add_ons ?? [],
    },
  ]

  packages.forEach(({ features }) => {
    features.forEach(feature => {
      const { feature_key, value_type } = feature
      const valueStr = String(feature.value)

      /*
      * da_vinci has no value_type, so we need to ignore it if value is false
      * Ignore da_vinci feature if value is false
      */
      if(valueStr == 'false' && feature_key == 'da_vinci'){
        return
      }


      /**
       * Ignore disabled boolean features.
       */
      if (
        value_type === "boolean" &&
        (valueStr === "false" || valueStr === "-1")
      ) {
        return
      }

      /**
       * Ignore limit features with zero value.
       */
      if (value_type === "limit" && valueStr === "0") {
        return
      }

      /**
       * Ignore lead_ads for customer preferred UID accounts.
       */
      if (
        isCustomerPreferredUid &&
        feature_key === "lead_ads"
      ) {
        return
      }

      /**
       * Deduplicate by feature key.
       * First occurrence wins.
       */
      if (!featureMap.has(feature_key)) {
        featureMap.set(feature_key, feature)
      }
    })
  })

  return Array.from(featureMap.values())
}
  
  export function checkFreetrailAccount(trialData: any, userStore: any, navigateToBilling: () => void) {
    /**
     * Initialize globally ONLY ONCE
     */
    useGlobalTrialState(
      trialData,
      userStore,
      {
        navigateToBilling,

        setIsTrailAccount: (value) => {
          if(value) {
            const features = userStore.getPackageFeatures;
            const Index = features.findIndex((feature: any) => feature.feature_key === 'da_vinci')
            if(Index !== -1) {
              features.splice(Index, 1)
              userStore.setPackageFeatures(JSON.parse(JSON.stringify(features)))
            }
          }
          userStore.setIsTrailAccount(value);
        },

        setIsTrialExpired: (value) => {
          userStore.setIsTrialExpired(value);
        },
      }
    );
  }